import React, { useState, useEffect, useRef } from 'react';
import './index.css';

// Use relative path for production (nginx proxy), full URL only for local dev
const API_URL = process.env.REACT_APP_API_URL || '';

function App() {
  const [agents, setAgents] = useState([]);
  const [activeAgentId, setActiveAgentId] = useState(null);
  const [conversations, setConversations] = useState({});
  const [inputValues, setInputValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessionId] = useState(() => Math.random().toString(36).substring(7));
  const [systemPrompts, setSystemPrompts] = useState({});
  const [showDebug, setShowDebug] = useState(false);
  const [suggestedQuestions, setSuggestedQuestions] = useState({});
  const [actionSuggestions, setActionSuggestions] = useState([]);
  const [introFetched, setIntroFetched] = useState(false);
  const introFetchedRef = useRef(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchAgents();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [conversations, activeAgentId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Generic starter questions for suspects (shown when conversation is empty)
  const defaultStarterQuestions = [
    'Vad gjorde du igår kväll?',
    'Kände du offret?',
    'Såg eller hörde du något ovanligt?'
  ];

  const fetchAgents = async () => {
    try {
      const response = await fetch(`${API_URL}/api/agents`);
      const data = await response.json();
      setAgents(data);
      if (data.length > 0) {
        // Set assistant as active if it exists, otherwise first agent
        const assistantAgent = data.find(a => a.id === 'assistant');
        setActiveAgentId(assistantAgent ? assistantAgent.id : data[0].id);
        const initialConversations = {};
        const initialInputs = {};
        const prompts = {};
        const initialSuggestions = {};
        data.forEach(agent => {
          initialConversations[agent.id] = [];
          initialInputs[agent.id] = '';
          prompts[agent.id] = agent.system_prompt || '';
          // Set starter questions for suspects (not assistant)
          if (agent.id !== 'assistant') {
            initialSuggestions[agent.id] = defaultStarterQuestions;
          }
        });
        setConversations(initialConversations);
        setInputValues(initialInputs);
        setSystemPrompts(prompts);
        setSuggestedQuestions(initialSuggestions);
        
        // Fetch intro message from assistant
        fetchIntro();
      }
    } catch (err) {
      setError('Failed to load agents');
      console.error(err);
    }
  };

  const fetchIntro = async () => {
    // Use ref to prevent double-fetch in React StrictMode
    if (introFetchedRef.current) return;
    introFetchedRef.current = true;
    setIntroFetched(true);
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/intro`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId })
      });
      const data = await response.json();
      
      if (data.response && !data.already_initialized) {
        setConversations(prev => ({
          ...prev,
          assistant: [...(prev.assistant || []), { role: 'assistant', displayName: 'Assistent', content: data.response }]
        }));
        setSuggestedQuestions(prev => ({ ...prev, assistant: data.suggested_questions || [] }));
        setActionSuggestions(data.action_suggestions || []);
      }
    } catch (err) {
      console.error('Failed to fetch intro:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssistantUpdate = async () => {
    if (loading) return;
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/assistant-update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId })
      });
      const data = await response.json();
      
      if (data.response && !data.no_update) {
        setConversations(prev => ({
          ...prev,
          assistant: [...(prev.assistant || []), { role: 'assistant', displayName: 'Assistent', content: data.response }]
        }));
        setSuggestedQuestions(prev => ({ ...prev, assistant: data.suggested_questions || [] }));
        setActionSuggestions(data.action_suggestions || []);
      }
    } catch (err) {
      console.error('Failed to fetch assistant update:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAgentSwitch = (agentId) => {
    const previousAgentId = activeAgentId;
    setActiveAgentId(agentId);
    
    // Fetch assistant update when switching TO assistant FROM a suspect (not from initial load)
    if (agentId === 'assistant' && previousAgentId && previousAgentId !== 'assistant') {
      fetchAssistantUpdate();
    }
    
    // Auto-greet for suspects on first contact (character introduces themselves)
    if (agentId !== 'assistant' && conversations[agentId] && conversations[agentId].length === 0) {
      fetchGreeting(agentId);
    }
  };

  const fetchGreeting = async (agentId) => {
    if (loading) return;
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/greet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, agent_id: agentId })
      });
      const data = await response.json();
      
      if (data.response && !data.already_greeted) {
        const displayName = agents.find(a => a.id === agentId)?.name || agentId;
        setConversations(prev => ({
          ...prev,
          [agentId]: [...(prev[agentId] || []), { role: 'assistant', displayName: displayName, content: data.response }]
        }));
        setSuggestedQuestions(prev => ({ ...prev, [agentId]: data.suggested_questions || [] }));
      }
    } catch (err) {
      console.error('Failed to fetch greeting:', err);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!activeAgentId || !inputValues[activeAgentId]?.trim()) return;

    const message = inputValues[activeAgentId].trim();
    
    setConversations(prev => ({
      ...prev,
      [activeAgentId]: [...prev[activeAgentId], { role: 'user', content: message }]
    }));

    setInputValues(prev => ({ ...prev, [activeAgentId]: '' }));
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          agent_id: activeAgentId,
          message: message
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send message');
      }
      const displayName = agents.filter(agent => agent.id === activeAgentId)[0].name;
      
      setConversations(prev => ({
        ...prev,
        [activeAgentId]: [...prev[activeAgentId], { role: 'assistant', displayName: displayName, content: data.response }]
      }));
      setSuggestedQuestions(prev => ({ ...prev, [activeAgentId]: data.suggested_questions || [] }));
      if (activeAgentId === 'assistant' && data.action_suggestions) {
        setActionSuggestions(data.action_suggestions);
      }
    } catch (err) {
      setError(err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = async (question) => {
    setSuggestedQuestions(prev => ({ ...prev, [activeAgentId]: [] }));
    setInputValues(prev => ({ ...prev, [activeAgentId]: question }));
    // Trigger send by simulating form submit
    const fakeEvent = { preventDefault: () => {} };
    setInputValues(prev => ({ ...prev, [activeAgentId]: question }));
    // Use timeout to ensure state is updated before sending
    setTimeout(() => document.querySelector('.input-container button[type="submit"]').click(), 0);
  };

  const handleActionClick = async (action) => {
    // Clear action suggestions and switch to the target agent
    setActionSuggestions([]);
    setActiveAgentId(action.agent_id);
    // Set the question as input and send it after agent switch
    setInputValues(prev => ({ ...prev, [action.agent_id]: action.question }));
    // Use timeout to ensure state is updated after agent switch
    setTimeout(() => document.querySelector('.input-container button[type="submit"]').click(), 50);
  };

  const handleInputChange = (e) => {
    setInputValues(prev => ({ ...prev, [activeAgentId]: e.target.value }));
  };

  if (agents.length === 0 && !error) {
    return <div className="loading">Laddar agenter...</div>;
  }

  return (
    <div className="app">
      <div className="tabs">
        {agents.map(agent => (
          <button
            key={agent.id}
            className={`tab ${activeAgentId === agent.id ? 'active' : ''}`}
            onClick={() => handleAgentSwitch(agent.id)}
          >
            {agent.name}
          </button>
        ))}
        <button
          className={`tab debug-toggle ${showDebug ? 'active' : ''}`}
          onClick={() => setShowDebug(!showDebug)}
        >
          🔧 Debug
        </button>
      </div>

      <div className="chat-container">
        <div className="messages">
          {showDebug && systemPrompts[activeAgentId] && (
            <div className="message system-prompt">
              <div className="message-role">System Prompt</div>
              <div className="message-content">{systemPrompts[activeAgentId]}</div>
            </div>
          )}
          {conversations[activeAgentId]?.map((msg, index) => (
            <div key={index} className={`message ${msg.role}`}>
              <div className="message-role">{msg.role === 'user' ? 'Du' : msg.displayName}</div>
              <div className="message-content">{msg.content}</div>
            </div>
          ))}
          {loading && (
            <div className="message assistant">
              <div className="message-role">{agents.find(a => a.id === activeAgentId)?.name}</div>
              <div className="typing-indicator"><span></span><span></span><span></span></div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {error && <div className="error">{error}</div>}

        {(suggestedQuestions[activeAgentId]?.length > 0) && (
          <div className="suggestions">
            {suggestedQuestions[activeAgentId].map((question, index) => (
              <button
                key={index}
                className="suggestion-btn"
                onClick={() => handleSuggestionClick(question)}
              >
                {question}
              </button>
            ))}
          </div>
        )}

        {(activeAgentId === 'assistant' && actionSuggestions.length > 0) && (
          <div className="action-suggestions">
            <span className="action-label">Tala med:</span>
            {actionSuggestions.map((action, index) => (
              <button
                key={index}
                className="action-btn"
                onClick={() => handleActionClick(action)}
              >
                <span className="action-agent">{action.agent_name}</span>
                <span className="action-question">"{action.question}"</span>
              </button>
            ))}
          </div>
        )}

        <form className="input-container" onSubmit={sendMessage}>
          <input
            type="text"
            value={inputValues[activeAgentId] || ''}
            onChange={handleInputChange}
            placeholder="Skriv ditt meddelande..."
            disabled={loading}
          />
          <button type="submit" disabled={loading || !inputValues[activeAgentId]?.trim()}>
            Skicka
          </button>
        </form>
      </div>
    </div>
  );
}

export default App;
